import type { TaskKind } from '../../interfaces/IScheduler.js';

// ---------------------------------------------------------------------------
// The lifecycle LADDER — one status vocabulary on screen, two state machines
// underneath (docs/specs/2026-09-18-task-commit-ux-design.md, decision 2).
//
// The task's PLAN status (planned / blocked / done), whether it is committed,
// and Trusstor's word for the activity (planned / inProgress / delayed /
// overdue / complete) fold into ONE rung the rail chip, the drawer header, the
// Activities page and the mobile app all render the same way. Rules it encodes:
//   · `committed` is a POSITION, not a status — a committed task is still
//     `planned` in the plan until somebody marks it done;
//   · everything between Committed and Done is the activity's status: for a
//     field activity Trusstor's word for what the IoT sees, for an
//     administrative one derived by the backend from its progress and dates;
//   · the last rung before Done is a SUGGESTION ("areas done"), never a
//     transition — done is human (invariant 6).
// ---------------------------------------------------------------------------

export {
  LADDER_RUNGS,
  ladderOf,
  rungOfActivityStatus,
  type ILadder,
  type ILadderActivity,
  type LadderRung,
  type LadderStep,
} from '../../../activity-ladder/index.js';
import type { ILadder, LadderRung } from '../../../activity-ladder/index.js';

// ---------------------------------------------------------------------------
// How a rung is WORN — the semantics both apps paint from. Each app keeps only
// the last step: a tone to a CSS class (web) or a pair of colours (mobile).
// ---------------------------------------------------------------------------

/** The i18n key of each rung's word. */
export const LADDER_LABEL_KEY: Record<LadderRung, string> = {
  planned: 'scheduler.ladder.planned',
  blocked: 'scheduler.ladder.blocked',
  committed: 'scheduler.ladder.committed',
  inProgress: 'scheduler.ladder.inProgress',
  delayed: 'scheduler.ladder.delayed',
  overdue: 'scheduler.ladder.overdue',
  areasDone: 'scheduler.ladder.areasDone',
  done: 'scheduler.ladder.done',
};

export type RungTone = 'planned' | 'blocked' | 'committed' | 'progress' | 'late' | 'areasDone' | 'done';

/** The colour each rung wears. Delayed and overdue share the late red — the
 *  flag ({@link rungMark}) keeps them apart without a second hue. In progress
 *  is the area board's yellow, so a working task reads as its working areas. */
export const RUNG_TONE: Record<LadderRung, RungTone> = {
  planned: 'planned',
  blocked: 'blocked',
  committed: 'committed',
  inProgress: 'progress',
  delayed: 'late',
  overdue: 'late',
  areasDone: 'areasDone',
  done: 'done',
};

/** The OUTLINED rungs — positions rather than states: committed (promised,
 *  nothing to report yet) and areas done (a human still has to say done). */
export const RUNG_OUTLINED: ReadonlySet<LadderRung> = new Set(['committed', 'areasDone']);

/**
 * The mark a chip carries before its word: overdue's FLAG, a filled point on
 * every other committed rung (a glance tells promised from planned), nothing
 * on the two rungs an uncommitted task stands on.
 */
export function rungMark(rung: LadderRung): 'flag' | 'point' | null {
  if (rung === 'overdue') return 'flag';
  return rung === 'planned' || rung === 'blocked' ? null : 'point';
}

/** The trail's first stop: a blocked task stands on Blocked, in Planned's place. */
export function firstStopOf(ladder: ILadder): 'planned' | 'blocked' {
  return ladder.rung === 'blocked' ? 'blocked' : 'planned';
}

/**
 * The word under the trail's Committed stop, as an i18n key — the activity's
 * status. A plain "committed" needs no second word (`null`), except on
 * administrative work, which has no field activity and says so.
 */
export function ladderTrailSubKey(ladder: ILadder, kind: TaskKind): string | null {
  if (ladder.step !== 1) return null;
  if (ladder.rung !== 'committed') return LADDER_LABEL_KEY[ladder.rung];
  return kind === 'administrative' ? 'scheduler.ladder.noTrusstor' : null;
}
